import React from "react";

import { css, cx } from "@emotion/css";
import Typography from "@mui/material/Typography";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { AtomProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";

export type BaseCardHeaderProps = {
  title: string | React.ReactNode;
  startIcon?: React.ReactNode;
  variant?: "plain" | "contained" | "banner";
  action?: React.ReactNode;
  onExit?: () => void;
};

export type CardHeaderProps = AtomProps<
  BaseCardHeaderProps,
  StyleClasses<"root" | "title" | "actions" | "exitButton" | "exitIcon">
>;

export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  startIcon,
  variant = "plain",
  action,
  onExit,
  className,
  classes,
  ...props
}) => {
  const styles = useStyles(makeStyles);

  return (
    <div
      className={cx(
        styles.root,
        {
          [styles.plain]: variant === "plain" || variant === "banner",
          "MuiCardHeader-plain": variant === "plain" || variant === "banner",
          [styles.contained]: variant === "contained",
          "MuiCardHeader-contained": variant === "contained",
        },
        className,
        classes?.root,
      )}
      {...props}
    >
      {typeof title === "string" ? (
        <>
          {startIcon}
          <Typography
            variant="h5"
            className={cx(
              {
                [styles.titlePlain]: variant === "plain",
                [styles.titleContained]: variant === "contained",
                [styles.titleBanner]: variant === "banner",
              },
              classes?.title,
            )}
          >
            {title}
          </Typography>
        </>
      ) : (
        <>
          {startIcon}
          {title}
        </>
      )}
      <div className={cx(styles.actions, classes?.actions)}>
        {action}
        {onExit && (
          <Button
            data-action="common.card.close"
            className={cx(styles.exitButton, classes?.exitButton)}
            variant="contained"
            startIcon={<Icons.exit className={cx(styles.exitIcon, classes?.exitIcon)} />}
            onClick={onExit}
          />
        )}
      </div>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  root: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: theme.spacing(8, 12, 0, 12),
    columnGap: theme.spacing(6),
    rowGap: theme.spacing(4),
  }),
  plain: css({}),
  contained: css({
    paddingBottom: theme.spacing(8),
    backgroundColor: theme.palette.background.default,
    alignSelf: "stretch",
  }),
  titlePlain: css({
    width: "100%",
    textAlign: "start",
    color: theme.palette.text.primary,
  }),
  titleContained: css({
    width: "100%",
    textAlign: "center",
    color: theme.palette.primary.main,
  }),
  titleBanner: css({
    width: "100%",
    textAlign: "start",
    color: theme.palette.text.primary,
    fontWeight: "bold",
    fontSize: FontSizes.body,
  }),
  exitButton: css({
    borderRadius: "100%",
    aspectRatio: "1",
    padding: theme.spacing(1),
  }),
  exitIcon: css({
    width: FontSizes.h4,
    height: FontSizes.h4,
  }),
  actions: css({
    display: "flex",
    flex: "1 1",
    flexDirection: "row",
    alignSelf: "stretch",
    justifyContent: "flex-end",
    gap: theme.spacing(8),
  }),
});
